import api from '@/services/api';
import router from '@/router/index';
import localStorage from '@/components/Services/storage';
import { store } from '@/store/store';
import { SMS_STATE_KEY } from '@/constants/auth';
import normalizeAustrianPhone from '@/services/phone';

export default {
    /**
     * Attempts to login a user via MSISDN login type
     *
     * @param   {Object}  state
     * @param   {Object}  commit
     * @param   {Object}  authData
     * @return  {void}
     */
    attemptToGetSmsCodeViaMsisdn({ commit }, authData) {
        const params = new URLSearchParams();
        // Always send the "43" country-code form (not "0043"), matching the checkout flow.
        const phone = normalizeAustrianPhone(authData.phone);
        localStorage.save('phone', phone, true);
        params.append('phone', phone);
        return api.post('/api/send-sms', params)
            .then((response) => {

                const isSmsCodeSent = response.data.code === 'SEND_SMS_TO_USER_SUCCESS';

                if (isSmsCodeSent) {
                    // Save SMS state as a single namespaced object
                    const smsState = {
                        codeSent: true,
                        userId: response.data.body.user.userId,
                        lastSentTime: Date.now(),
                        phone,
                    };
                    localStorage.save(SMS_STATE_KEY, JSON.stringify(smsState), true);
                }

                commit('GET_SMS_CODE', {
                    userId: response.data.body.user.userId,
                    isSmsCodeSent,
                    phoneNumber: response.data.body.user.phoneNumber,
                });
                return response;
            })
            .catch((error) => {
                if (error.response.data.code === 'SEND_SMS_TO_USER_FAILED_USER_DOESNT_EXIST') {
                    commit('SHOW_LOGIN_ERRORS', {
                        body: {
                            validation_errors:
                                {
                                    phone: [error.response.data.body.message],
                                },
                        },
                    });
                } else if (error.response.data.code === 'REQUEST_FAILED_SERVER_ERROR') {
                    commit('SHOW_LOGIN_ERRORS', {
                        body: {
                            validation_errors:
                                {
                                    phone: [error.response.data.body.message],
                                },
                        },
                    });
                } else {
                    commit('SHOW_LOGIN_ERRORS', error.response.data);
                }
                throw error;
            });
    },

    /**
     * Attempts to login a user via UserName and Password
     *
     * @param context
     * @param {object} authData
     */
    loginViaPassword(context, authData) {
        const params = new URLSearchParams();
        params.append('username', authData.username);
        params.append('password', authData.password);
        api.post('/api/password-login', params)
            .then((response) => {
                const token = response.data.body.token;
                context.commit('STORE_USER', {
                    token,
                });
                localStorage.save('token', token, true);
                // Check contract type and redirect user to home page or tv page
                context.commit('CHECK_FIRST_CONTRACT_TYPE');
            })
            .catch((error) => {
                context.commit('SHOW_LOGIN_ERRORS', error.response.data);
            });
    },

    /**
     * Attempts to login a user via Sms code login type
     *
     * @param context
     * @param {object} authData
     */
    loginViaSms(context, authData) {
        const params = new URLSearchParams();
        params.append('id', authData.userId);
        params.append('code', authData.code);
        return api.post('api/sms-login', params)
            .then((response) => {
                const token = response.data.body.token;
                context.commit('STORE_USER', {
                    token,
                });
                localStorage.save('token', token, true);
                // Check contract type and redirect user to home page or tv page
                context.commit('CHECK_FIRST_CONTRACT_TYPE');
                return response;
            })
            .catch((error) => {
                console.log(error);
                if (error.response.data.code === 'SMS_LOGIN_USER_FAILED_INVALID_CODE') {
                    context.commit('SHOW_LOGIN_ERRORS', {
                        body: {
                            validation_errors:
                                {
                                    code: [error.response.data.body.message],
                                },
                        },
                    });
                } else {
                    context.commit('SHOW_LOGIN_ERRORS', error.response.data);
                }
                throw error;
            });
    },

    /**
     * Attempts to logout user
     *
     * @param context
     */
    logout(context) {
        context.commit('CLEAR_AUTH_DATA');
        context.commit('contract/CLEAR_CONTRACT_DATA', {}, { root: true });
        localStorage.clear();
        router.replace('/login');
    },

    /**
     * Checks if user is logged in (has valid token)
     *
     * @param state
     * @param payload
     * @return  {void}
     */
    tryAutoLogin({ commit }, payload) {
        api.get('/api/me'+(localStorage.get('phone', true) ? '/'+localStorage.get('phone', true) : ''))
            .then(() => {
                commit('STORE_USER', {
                    token: payload.token,
                });
            })
            .catch(() => {
                // Token expired or invalid token
                localStorage.remove('token');
            });
    },

    /**
     * Get user main data when user log in
     *
     * @param context
     */
    getUserData(context) {
        api.get('/api/me'+(localStorage.get('phone', true) ? '/'+localStorage.get('phone', true) : ''))
            .then((response) => {
                context.commit('STORE_MAIN_USER_DATA', {
                    data: response.data.body.user,
                });
                context.commit('contract/SET_USER_CONTRACTS', {
                    contracts: response.data.body.user.contracts,
                }, { root: true });

                context.commit('notification/SET_NUMBER_UNREAD_NOTIFICATION', {
                    number: response.data.body.user.unreadNotifications,
                }, { root: true });
            })
            .then(() => {
                store.dispatch('setGdpr');
            })
            .catch(() => {
                // Token expired or invalid token
                localStorage.remove('token');
            });
    },

    /**
     * Check first contract is mobile or tv and redirect user to that page
     *
     */
    checkFirstContractType(context) {
        const firstContract = context.rootState.contract.clickedContract;
        if (firstContract.isTv) {
            router.replace('/tv');
        } else if (firstContract.isInternet) {
            router.replace('/internet');
        } else {
            router.replace('/');
        }
    },

    /**
     * Set user data when user activate tariff, bundles..
     * @param context
     */
    setUserDataAfterUpdating(context) {
        api.get('/api/me'+(localStorage.get('phone', true) ? '/'+localStorage.get('phone', true) : ''))
            .then((response) => {
                context.commit('STORE_MAIN_USER_DATA', {
                    data: response.data.body.user,
                });
            });
    },

    /**
     * Restore SMS state from localStorage
     *
     * @param {Object} context
     * @param {Object} payload - Contains userId, isSmsCodeSent, phoneNumber
     * @return {void}
     */
    restoreSmsState({ commit }, payload) {
        commit('GET_SMS_CODE', payload);
    },
};
